import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SecurityDashboard = () => {
  const [securityData, setSecurityData] = useState({
    activeSessions: 1,
    recentPrivilegeEscalations: 0,
    suspiciousActivities: 0,
    lastSecurityScan: 'Just now',
    mfaStatus: 'Active'
  });

  const securityMetrics = [
    {
      label: 'Active Admin Sessions',
      value: securityData?.activeSessions,
      icon: 'Users',
      status: 'success',
      description: 'Current authenticated administrators'
    },
    {
      label: 'Privilege Escalations',
      value: securityData?.recentPrivilegeEscalations,
      icon: 'ShieldAlert',
      status: securityData?.recentPrivilegeEscalations > 0 ? 'warning' : 'success',
      description: 'Last 24 hours'
    },
    {
      label: 'Suspicious Activities',
      value: securityData?.suspiciousActivities,
      icon: 'AlertTriangle',
      status: securityData?.suspiciousActivities > 0 ? 'error' : 'success',
      description: 'Flagged for review'
    },
    {
      label: 'MFA Status',
      value: securityData?.mfaStatus,
      icon: 'ShieldCheck',
      status: 'success',
      description: 'Multi-factor authentication'
    }
  ];

  const statusColors = {
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    error: 'bg-red-50 border-red-200 text-red-700'
  };

  const iconColors = {
    success: 'bg-green-100 text-green-600',
    warning: 'bg-yellow-100 text-yellow-600',
    error: 'bg-red-100 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Icon name="Shield" className="w-5 h-5 text-primary" />
            Security Dashboard
          </h2>
          <p className="text-gray-600 text-sm mt-1">Real-time security monitoring and access control</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-600">All Systems Secure</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {securityMetrics?.map((metric, index) => (
          <div
            key={index}
            className={`${statusColors?.[metric?.status]} border rounded-lg p-4 transition-all hover:shadow-md`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 ${iconColors?.[metric?.status]} rounded-lg flex items-center justify-center`}>
                <Icon name={metric?.icon} className="w-5 h-5" />
              </div>
              {metric?.status === 'success' && (
                <Icon name="CheckCircle" className="w-5 h-5 text-green-600" />
              )}
              {metric?.status === 'warning' && (
                <Icon name="AlertCircle" className="w-5 h-5 text-yellow-600" />
              )}
              {metric?.status === 'error' && (
                <Icon name="XCircle" className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div className="text-2xl font-bold mb-1">{metric?.value}</div>
            <div className="text-sm font-medium mb-1">{metric?.label}</div>
            <div className="text-xs opacity-75">{metric?.description}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Icon name="Clock" className="w-4 h-4" />
            <span>Last security scan: {securityData?.lastSecurityScan}</span>
          </div>
          <button className="text-primary hover:text-primary/80 font-medium flex items-center gap-1">
            View Full Security Report
            <Icon name="ExternalLink" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;